import { Component, inject, input } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { type CellContext, injectFlexRenderContext } from '@tanstack/angular-table';
import { deriveLanguageLoadedSignal, resolveEnumLabel } from '../../../lib/resolve-enum-label';

@Component({
  selector: 'au-enum-cell',
  template: `
    {{ label }}
  `,
})
export class EnumCell<T = unknown> {
  readonly prefix = input.required<string>();

  private readonly translator = inject(TranslocoService);
  private readonly context = injectFlexRenderContext<CellContext<T, string | null | undefined>>();
  private readonly languageLoaded = deriveLanguageLoadedSignal(this.translator, this.prefix);

  /**
   * A getter, NOT a `computed()`. `injectFlexRenderContext()` returns a Proxy whose `getValue()`
   * is a plain method rather than a signal, so a `computed` tracks no dependency on the row's
   * value — it only tracks `languageLoaded()` — and caches the FIRST label forever. FlexRender
   * reuses this same component instance across rows and only marks it dirty for a fresh change
   * detection pass, so a cached `computed()` kept showing the previous row's label after a
   * search or a page change (T-209). The getter re-runs on every render flexRender triggers, and
   * still reads `languageLoaded()` so a runtime language switch keeps recomputing it too.
   */
  protected get label(): string {
    this.languageLoaded();
    return resolveEnumLabel(this.translator, this.prefix(), this.context.getValue());
  }
}
